import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/Shared";
import { AuthShell } from "@/components/AuthShell";
import { publicApi, formBody, errMsg } from "@/lib/api";

const InvalidLink = () => (
  <AuthShell title="Link expired">
    <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 p-5 flex items-start gap-3" data-testid="reset-password-invalid">
      <AlertCircle className="w-6 h-6 text-red-700 shrink-0" />
      <p className="text-red-900">This reset link is invalid, has expired, or was already used. Please request a new one.</p>
    </div>
    <Link to="/forgot-password" className="mt-btn-primary w-full mt-6" data-testid="reset-password-request-new">
      Request a New Link
    </Link>
  </AuthShell>
);

// Token can arrive as /reset-password/<token>, ?token= or #token=; strip anything a mail client may append.
const readToken = (pathToken, params) => {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const raw = pathToken || params.get("token") || hash.get("token") || "";
  return raw.replace(/[^A-Za-z0-9_-]/g, "");
};

async function checkToken(token) {
  for (let i = 0; i < 3; i++) {
    try {
      const { data } = await publicApi.get("/auth/reset-password/verify", { params: { token } });
      if (typeof data?.valid === "boolean") return data.valid;
    } catch (e) {
      if (e.response?.status && e.response.status < 500) break;
    }
    await new Promise((r) => setTimeout(r, 800));
  }
  return true; // couldn't confirm; let the reset request itself decide
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const { token: pathToken } = useParams();
  const token = readToken(pathToken, params);
  const navigate = useNavigate();
  const [valid, setValid] = useState(null);
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return setValid(false);
    let active = true;
    checkToken(token).then((ok) => active && setValid(ok));
    return () => {
      active = false;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) return setError("Password must be at least 6 characters.");
    if (form.password !== form.confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await publicApi.post("/auth/reset-password", formBody({ token, password: form.password }));
      toast.success("Password updated. Log in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      if (err.response?.status === 400) setValid(false);
      else if (!err.response) setError("Couldn't reach MileageTax. Please check your connection and try again.");
      else setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (valid === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="reset-password-loading">
        <div className="w-10 h-10 rounded-full border-4 border-emerald-200 border-t-emerald-700 animate-spin" />
      </div>
    );
  if (!valid) return <InvalidLink />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <AuthShell title="Set a new password" subtitle="Choose a new password for your MileageTax account.">
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-password-form">
        <Field label="New password" htmlFor="rp-password" hint="At least 6 characters">
          <Input id="rp-password" type="password" autoComplete="new-password" required minLength={6} value={form.password} onChange={set("password")} className="h-12 text-base" data-testid="reset-password-input" />
        </Field>
        <Field label="Confirm new password" htmlFor="rp-confirm">
          <Input id="rp-confirm" type="password" autoComplete="new-password" required value={form.confirm} onChange={set("confirm")} className="h-12 text-base" data-testid="reset-password-confirm-input" />
        </Field>
        {error && (
          <p className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5" data-testid="reset-password-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={loading} className="mt-btn-primary w-full" data-testid="reset-password-submit-button">
          {loading && <Loader2 className="w-5 h-5 animate-spin" />}
          Update Password
        </button>
      </form>
    </AuthShell>
  );
}
